import {useMemo} from "react"
import {Badge, Button, Card, Dropdown} from "react-bootstrap"
import BootstrapTable from "react-bootstrap-table-next"
import filterFactory, {
    selectFilter,
    textFilter,
} from "react-bootstrap-table2-filter"

export type PlanningTodo = {
    category: "SOURCE" | "GEOMETRY" | "MEASUREMENT" | "POWER" | "EXAM_LAYOUT"
    title: string
    detail: string
    count: number
}

export type PlanningOverviewRoom = {
    room: {
        _id: string
        identifier: string
        name: string
        building?: {identifier: string; name?: string}
        tucanOverviewUrl?: string
        operationalStatus?: "active" | "inactive"
    }
    configured: boolean
    pdfCount: number
    documents: Array<{
        _id: string
        filename: string
        sourceLabel?: string
        bytes: number
        publicRoomCodes: string[]
        variants: Array<{
            variant: number
            totalVariants: number
            examCapacity: number
            physicalCapacity: number
        }>
        interpretationStatus:
            "CATALOGUED" | "AUTO_EXTRACTED" | "VERIFIED" | "NEEDS_REVIEW"
        extractionWarnings: string[]
    }>
    seatCount: number
    layoutCount: number
    outletCount: number
    todoCount: number
    todos: PlanningTodo[]
}

export type PlanningOverview = {
    summary: {
        roomCount: number
        roomsWithPdf: number
        configuredRooms: number
        completeRooms: number
        todoCount: number
        categoryCounts: Record<string, number>
    }
    rooms: PlanningOverviewRoom[]
}

type OverviewRow = PlanningOverviewRoom & {
    id: string
    buildingLabel: string
    roomIdentifier: string
    roomName: string
    state: "COMPLETE" | "CONFIGURED" | "SOURCE_ONLY" | "EMPTY"
    todoLabel: string
}

const stateLabel = (state: OverviewRow["state"]) => ({
    COMPLETE: "Vollständig",
    CONFIGURED: "In Bearbeitung",
    SOURCE_ONLY: "Nur PDF vorhanden",
    EMPTY: "Nicht begonnen",
}[state])

export default function RoomPowerPlanningOverviewComponent({
    overview,
    onOpenRoom,
    onOpenPdf,
    onUploadPdf,
    onSetOperationalStatus,
    onPurgePlanningData,
    openingDocumentId,
}: {
    overview: PlanningOverview
    onOpenRoom: (room: PlanningOverviewRoom) => void
    onOpenPdf: (document: PlanningOverviewRoom["documents"][number]) => void
    onUploadPdf: (room: PlanningOverviewRoom) => void
    onSetOperationalStatus: (
        room: PlanningOverviewRoom,
        status: "active" | "inactive"
    ) => void
    onPurgePlanningData: (room: PlanningOverviewRoom) => void
    openingDocumentId: string | null
}) {
    const rows = useMemo<OverviewRow[]>(() => overview.rooms.map(item => {
        const inactive = item.room.operationalStatus === "inactive"
        const state: OverviewRow["state"] = !inactive && item.todos.length === 0
            ? "COMPLETE" : item.configured
                ? "CONFIGURED" : item.pdfCount > 0 ? "SOURCE_ONLY" : "EMPTY"
        return {
            ...item,
            id: item.room._id,
            buildingLabel: item.room.building?.identifier ?? "",
            roomIdentifier: item.room.identifier,
            roomName: item.room.name,
            state,
            todoLabel: inactive
                ? "Deaktiviert"
                : item.todos.map(todo =>
                    `${todo.title} ${todo.detail}`).join(" "),
        }
    }), [overview.rooms])
    const columns = useMemo(() => [
        {
            dataField: "buildingLabel",
            text: "Gebäude",
            sort: true,
            filter: textFilter({placeholder: "Filter"}),
            headerStyle: {width: "4.5rem"},
        },
        {
            dataField: "roomIdentifier",
            text: "Raum",
            sort: true,
            filter: textFilter({placeholder: "Filter"}),
            headerStyle: {width: "5rem"},
        },
        {
            dataField: "roomName",
            text: "Bezeichnung",
            sort: true,
            filter: textFilter({placeholder: "Filter"}),
            headerStyle: {width: "10rem"},
        },
        {
            dataField: "pdfCount",
            text: "PDFs",
            sort: true,
            headerAlign: "center",
            align: "center",
            filter: textFilter({placeholder: "#"}),
            headerStyle: {width: "3.5rem"},
        },
        {
            dataField: "seatCount",
            text: "Sitze",
            sort: true,
            headerAlign: "center",
            align: "center",
            filter: textFilter({placeholder: "#"}),
            headerStyle: {width: "3.75rem"},
        },
        {
            dataField: "layoutCount",
            text: "Aufteilungen",
            sort: true,
            headerAlign: "center",
            align: "center",
            filter: textFilter({placeholder: "#"}),
            headerStyle: {width: "5.5rem"},
        },
        {
            dataField: "state",
            text: "Bearbeitungsstand",
            sort: true,
            filter: selectFilter({
                placeholder: "Alle",
                options: {
                    COMPLETE: "Vollständig",
                    CONFIGURED: "In Bearbeitung",
                    SOURCE_ONLY: "Nur PDF vorhanden",
                    EMPTY: "Nicht begonnen",
                },
            }),
            headerStyle: {width: "7.5rem"},
            formatter: (state: OverviewRow["state"]) =>
                <Badge bg={state === "COMPLETE" ? "success"
                    : state === "CONFIGURED" ? "primary"
                        : state === "SOURCE_ONLY" ? "info" : "secondary"}>
                    {stateLabel(state)}
                </Badge>,
        },
        {
            dataField: "todoLabel",
            text: "Status",
            sort: true,
            filter: textFilter({placeholder: "Filter"}),
            headerStyle: {width: "11rem"},
            formatter: (_cell: string, row: OverviewRow) =>
                row.room.operationalStatus === "inactive"
                    ? <Badge bg="danger">Deaktiviert</Badge>
                    : row.todos.length === 0
                    ? <span className="text-muted">Keine offenen Punkte</span>
                    : <div className="d-flex flex-wrap gap-1">
                        {row.todos.map(todo =>
                            <Badge bg="warning" text="dark"
                                key={`${todo.category}-${todo.title}`}>
                                {todo.title}
                                {todo.count > 1 && ` (${todo.count})`}
                            </Badge>)}
                    </div>,
        },
        {
            dataField: "actions",
            text: "Aktionen",
            isDummyField: true,
            headerStyle: {width: "11.5rem"},
            formatter: (_cell: unknown, row: OverviewRow) =>
                <div className="d-flex flex-wrap justify-content-end gap-1">
                    {row.documents.length === 0
                        ? <Button size="sm" variant="outline-secondary"
                            onClick={() => onUploadPdf(row)}>
                            <i className="bi bi-file-earmark-plus me-1"/>
                            PDF hinzufügen
                        </Button>
                        : <Dropdown>
                        <Dropdown.Toggle size="sm" variant="outline-secondary">
                            <i className="bi bi-file-earmark-pdf me-1"/>
                            {row.documents.length === 1
                                ? "PDF" : `PDFs (${row.documents.length})`}
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                            {row.documents.map(document =>
                                <Dropdown.Item key={document._id}
                                    disabled={openingDocumentId === document._id}
                                    onClick={() => onOpenPdf(document)}>
                                    <i className="bi bi-file-earmark-pdf me-2"/>
                                    {document.filename}
                                </Dropdown.Item>)}
                            <Dropdown.Divider/>
                            <Dropdown.Item onClick={() => onUploadPdf(row)}>
                                <i className="bi bi-file-earmark-plus me-2"/>
                                Weiteres PDF hinzufügen
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>}
                    {row.room.tucanOverviewUrl &&
                        <Button as="a" size="sm" variant="outline-info"
                            href={row.room.tucanOverviewUrl}
                            target="_blank" rel="noopener noreferrer">
                            <i className="bi bi-box-arrow-up-right me-1"/>
                            TUCaN
                        </Button>}
                    <Button size="sm" variant="outline-primary"
                        onClick={() => onOpenRoom(row)}>
                        <i className="bi bi-box-arrow-in-right me-1"/>Öffnen
                    </Button>
                    <Dropdown>
                        <Dropdown.Toggle size="sm" variant="outline-secondary"
                            aria-label="Weitere Raumaktionen">
                            <i className="bi bi-three-dots"/>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                            {(row.room.operationalStatus ?? "active") === "inactive"
                                ?
                                <Dropdown.Item onClick={() =>
                                    onSetOperationalStatus(row, "active")}>
                                    Aktivieren
                                </Dropdown.Item>
                                : <Dropdown.Item onClick={() =>
                                    onSetOperationalStatus(row, "inactive")}>
                                    Deaktivieren
                                </Dropdown.Item>}
                            <Dropdown.Divider/>
                            <Dropdown.Item className="text-danger"
                                onClick={() => onPurgePlanningData(row)}>
                                Planungsdaten löschen
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>,
        },
    ], [
        onOpenPdf,
        onOpenRoom,
        onUploadPdf,
        onSetOperationalStatus,
        onPurgePlanningData,
        openingDocumentId,
    ])

    return <Card className="mb-3 room-power-planning-overview">
        <Card.Body>
            <BootstrapTable
                keyField="id"
                data={rows}
                columns={columns}
                filter={filterFactory()}
                defaultSorted={[{
                    dataField: "buildingLabel",
                    order: "asc",
                }]}
                striped
                hover
                condensed
                bootstrap4
                noDataIndication="Keine Hörsäle gefunden"
            />
        </Card.Body>
    </Card>
}
